import { useState } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { useAnnotationStore } from '../../stores/annotationStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { formatFileSize } from '../../utils/fileUtils';
import './StatusBar.css';

export function StatusBar() {
  const { currentPage, totalPages, zoom, documents, activeDocumentId, spreadMode, setSpreadMode, scrollMode, setScrollMode } = useDocumentStore();
  const { activeTool } = useAnnotationStore();
  const { theme, toggleFullscreen, isFullscreen } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const [pageInput, setPageInput] = useState('');

  const activeDoc = documents.find((d) => d.id === activeDocumentId);

  const handlePageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(pageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      useDocumentStore.getState().setCurrentPage(page);
    }
    setPageInput('');
  };

  const toolLabels: Record<string, string> = {
    select: 'Select',
    hand: 'Hand Tool',
    'text-select': 'Text Select',
    highlight: 'Highlight',
    underline: 'Underline',
    strikethrough: 'Strikethrough',
    'text-annotation': 'Text Box',
    'sticky-note': 'Sticky Note',
    freehand: 'Pencil',
    rectangle: 'Rectangle',
    circle: 'Circle',
    arrow: 'Arrow',
    line: 'Line',
    stamp: 'Stamp',
    signature: 'Signature',
    'measure-distance': 'Measure',
    'measure-area': 'Area',
    redact: 'Redact',
    eraser: 'Eraser',
  };

  return (
    <div className="statusbar">
      <div className="statusbar-left">
        {activeDoc && (
          <>
            <div className="page-navigation" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                className="btn-icon btn-ghost"
                onClick={() => useDocumentStore.getState().prevPage()}
                disabled={currentPage <= 1}
                title="Previous Page"
                style={{ fontSize: '12px', width: '24px', height: '24px', padding: 0 }}
              >
                ◀
              </button>
              <form onSubmit={handlePageSubmit} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span className="statusbar-label">Page</span>
                <input
                  type="text"
                  className="page-input"
                  value={pageInput || currentPage}
                  onChange={(e) => setPageInput(e.target.value)}
                  onFocus={(e) => {
                    setPageInput(String(currentPage));
                    e.target.select();
                  }}
                  onBlur={() => setPageInput('')}
                />
                <span className="statusbar-label">of {totalPages}</span>
              </form>
              <button
                className="btn-icon btn-ghost"
                onClick={() => useDocumentStore.getState().nextPage()}
                disabled={currentPage >= totalPages}
                title="Next Page"
                style={{ fontSize: '12px', width: '24px', height: '24px', padding: 0 }}
              >
                ▶
              </button>
            </div>
            <div className="divider" />
            <span className="statusbar-info">{formatFileSize(activeDoc.fileSize)}</span>
          </>
        )}
      </div>

      <div className="statusbar-center">
        {activeDoc && (
          <span className="statusbar-tool">
            Tool: <strong>{toolLabels[activeTool] || activeTool}</strong>
          </span>
        )}
      </div>

      <div className="statusbar-right">
        {activeDoc && (
          <>
            <div className="layout-controls" style={{ display: 'flex', gap: '4px', marginRight: 'var(--space-2)' }}>
              <button
                className={`btn-icon btn-ghost ${scrollMode === 'vertical' ? 'active' : ''}`}
                onClick={() => setScrollMode('vertical')}
                title="Continuous Scroll"
                style={{ fontSize: '14px', width: '24px', height: '24px', padding: 0 }}
              >
                ↕
              </button>
              <button
                className={`btn-icon btn-ghost ${scrollMode === 'single' ? 'active' : ''}`}
                onClick={() => setScrollMode('single')}
                title="Single Page Mode"
                style={{ fontSize: '14px', width: '24px', height: '24px', padding: 0 }}
              >
                ☐
              </button>
              <div className="divider" />
              <button
                className={`btn-icon btn-ghost ${spreadMode === 'none' ? 'active' : ''}`}
                onClick={() => setSpreadMode('none')}
                title="Single Page View"
                style={{ fontSize: '14px', width: '24px', height: '24px', padding: 0 }}
              >
                📄
              </button>
              <button
                className={`btn-icon btn-ghost ${spreadMode === 'odd' ? 'active' : ''}`}
                onClick={() => setSpreadMode('odd')}
                title="Two Page View"
                style={{ fontSize: '14px', width: '24px', height: '24px', padding: 0 }}
              >
                📖
              </button>
              <div className="divider" />
              <button
                className={`btn-icon btn-ghost ${isFullscreen ? 'active' : ''}`}
                onClick={toggleFullscreen}
                title="Full Screen"
                style={{ fontSize: '14px', width: '24px', height: '24px', padding: 0 }}
              >
                {isFullscreen ? '⛶' : '⛶'}
              </button>
            </div>
            <div className="divider" />
          </>
        )}
        <span className="statusbar-info">{Math.round(zoom * 100)}%</span>
        <div className="divider" />
        {user && (
          <>
            <span className="statusbar-info" style={{ textTransform: 'capitalize' }}>
              {user.role}
            </span>
            <div className="divider" />
          </>
        )}
        <span className="statusbar-info status-dot">
          <span className={`dot ${theme === 'dark' ? 'dot-purple' : 'dot-blue'}`} />
          {theme === 'dark' ? 'Dark' : 'Light'}
        </span>
      </div>
    </div>
  );
}
